/**
 * The fleet, as the office edits it.
 *
 * The types are the API's own, from the generated schema, narrowed with `Narrow` where the
 * screens switch over a state or a kind.
 *
 * The one thing worth knowing before reading the screens: a driver here is two things in two
 * databases. An account with a role, which is global, and a record in this firm's own schema.
 * Hiring creates both; the API composes them, and nothing on this side has to know.
 */

import { request, type Narrow, type Schema } from '@taxi/api-client';

import type { ThemePreference } from '../api';

/** Where a driver is right now. Matches the backend's `ShiftState`. */
export type ShiftState = 'OffDuty' | 'Available' | 'Busy' | 'OnBreak';

/** What a document belongs to. */
export type DocumentOwner = 'Driver' | 'Vehicle';

/**
 * The kinds of paperwork the system records.
 *
 * Split by owner because the form is one or the other, never both, and offering a driver a
 * roadworthiness certificate is how a document ends up on the wrong record.
 */
export const driverDocumentKinds = [
  'DrivingLicence',
  'TaxiDriverCertificate',
  'MedicalCertificate',
  'Other',
] as const;

export const vehicleDocumentKinds = [
  'VehicleRegistration',
  'Insurance',
  'RoadworthinessCertificate',
  'TaxiPermit',
  'Other',
] as const;

export type DocumentKind =
  | (typeof driverDocumentKinds)[number]
  | (typeof vehicleDocumentKinds)[number];

/**
 * The two a shift is refused without.
 *
 * Repeated from the backend's `FleetDocument.RequiredForDriver` and `RequiredForVehicle`
 * only so the screen can mark them, never to decide anything. `ShiftRules` is the authority
 * and refuses at sign-on whatever this array says.
 */
export const requiredKinds: Record<DocumentOwner, DocumentKind> = {
  Driver: 'DrivingLicence',
  Vehicle: 'Insurance',
};

export type FleetDocument = Narrow<Schema<'FleetDocumentView'>, { ownerType: DocumentOwner; kind: DocumentKind }>;

/**
 * What the office has decided about one driver.
 *
 * A settings **document** rather than a record named after the field it holds today. The
 * next per-driver setting should be a property here and a row in the same form, never a
 * second route and a second screen.
 */
export type DriverSettings = Schema<'DriverSettingsResponse'>;

export type FleetDriver = Narrow<
  Schema<'FleetDriver'>,
  { shiftState: ShiftState; documents: FleetDocument[]; appTheme: ThemePreference | null }
>;

export type FleetVehicle = Narrow<Schema<'FleetVehicle'>, { documents: FleetDocument[] }>;

/** A document about to stop somebody working. */
export type ExpiringDocument = Schema<'ExpiringDocument'>;

export type FleetCategory = Schema<'FleetCategory'>;

/** The fleet's one switch: who may drive what (#370). */
export type FleetSettings = Schema<'FleetSettings'>;

/** Something a car offers beside its class. Any number per car. */
export type FleetFeature = Schema<'FleetFeature'>;

export type NewDriver = Schema<'NewDriver'>;

export type HireRequest = Schema<'HireRequest'>;

/** Null when the address already had a password, so nothing was sent. */
export type Hired = Schema<'Hired'>;

/** False when the person holds another role here, so their console access was left alone. */
export type DriverStatus = Schema<'DriverStatus'>;

export type NewVehicle = Schema<'NewVehicle'>;

export type NewDocument = Schema<'NewDocument'>;

const base = '/api/tenant/fleet';

export const fleetApi = {
  drivers: () => request<FleetDriver[]>(`${base}/drivers`),

  hire: (body: HireRequest) =>
    request<Hired>(`${base}/drivers`, { method: 'POST', body }),

  updateDriver: (driverId: string, body: NewDriver) =>
    request<FleetDriver>(`${base}/drivers/${driverId}`, { method: 'PUT', body }),

  setDriverActive: (driverId: string, active: boolean) =>
    request<DriverStatus>(`${base}/drivers/${driverId}/status`, {
      method: 'POST',
      body: { active },
    }),

  /**
   * One driver's settings, from the roster path rather than from this one.
   *
   * `/api/tenant/drivers` is where the backend put them, beside the roster that exists so a
   * settings screen has somebody to pick from. Reached from here because this is the screen
   * that picks.
   */
  driverSettings: (driverId: string) =>
    request<DriverSettings>(`/api/tenant/drivers/${driverId}/settings`),

  saveDriverSettings: (driverId: string, settings: { appTheme: ThemePreference | null }) =>
    request<DriverSettings>(`/api/tenant/drivers/${driverId}/settings`, {
      method: 'PUT',
      body: settings,
    }),

  vehicles: () => request<FleetVehicle[]>(`${base}/vehicles`),

  /** The cars the office allows one driver, replacing the list (#370). */
  setAllowedVehicles: (driverId: string, vehicleIds: string[]) =>
    request<Schema<'AllowedVehicles'>>(`${base}/drivers/${driverId}/vehicles`, {
      method: 'PUT',
      body: { vehicleIds },
    }),

  /** Whether drivers may take any free car, or only the ones the office allowed. */
  settings: () => request<FleetSettings>('/api/tenant/settings/fleet/'),

  saveSettings: (body: FleetSettings) =>
    request<FleetSettings>('/api/tenant/settings/fleet/', { method: 'PUT', body }),

  registerVehicle: (body: NewVehicle) =>
    request<FleetVehicle>(`${base}/vehicles`, { method: 'POST', body }),

  updateVehicle: (vehicleId: string, body: NewVehicle) =>
    request<FleetVehicle>(`${base}/vehicles/${vehicleId}`, { method: 'PUT', body }),

  setVehicleActive: (vehicleId: string, active: boolean) =>
    request<FleetVehicle>(`${base}/vehicles/${vehicleId}/status`, {
      method: 'POST',
      body: { active },
    }),

  recordDocument: (body: NewDocument) =>
    request<FleetDocument>(`${base}/documents`, { method: 'POST', body }),

  removeDocument: (documentId: string) =>
    request<void>(`${base}/documents/${documentId}`, { method: 'DELETE' }),

  expiring: () => request<ExpiringDocument[]>(`${base}/expiring`),

  categories: () => request<FleetCategory[]>(`${base}/categories`),

  features: () => request<FleetFeature[]>(`${base}/features`),
};
